import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeader } from "@/hooks/usePageHeader";
import { CalendarGrid } from "@/components/calendar/CalendarGrid";
import { CreateProjectDialog } from "@/components/calendar/CreateProjectDialog";
import { useProjects, ProjectWithRelations } from "@/hooks/useProjects";
import { usePeople } from "@/hooks/usePeople";
import { useAllEmployeeAbsences } from "@/hooks/useEmployeeAbsences";
import {
  useEmployeeFunctionAssignments,
  useEmployeeBookedAvailability,
  useProjectIdsWithFunctions,
} from "@/hooks/usePersonBookings";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarSync, Copy, Loader2 } from "lucide-react";
import { useCalendarFeedUrl } from "@/hooks/useCalendarFeed";
import { toast } from "sonner";

export default function ProjectCalendarPage() {
  const navigate = useNavigate();
  const { data: projects, isLoading } = useProjects();
  const { data: employees } = usePeople("employee");
  const { data: absences } = useAllEmployeeAbsences();
  const employeeIds = employees?.map((e) => e.id);
  const { data: bookings } = useEmployeeFunctionAssignments(employeeIds);
  const { data: bookedAvailability } =
    useEmployeeBookedAvailability(employeeIds);
  const { data: projectIdsWithFunctions } = useProjectIdsWithFunctions();
  const { isEmployee } = useAuth();
  const calendarFeed = useCalendarFeedUrl();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [visiblePeople, setVisiblePeople] = useState<string[] | null>(() => {
    try {
      const stored = localStorage.getItem("calendar-visible-people");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const handleVisiblePeopleChange = (people: string[] | null) => {
    setVisiblePeople(people);
    if (people) {
      localStorage.setItem("calendar-visible-people", JSON.stringify(people));
    } else {
      localStorage.removeItem("calendar-visible-people");
    }
  };

  const handleProjectClick = (project: ProjectWithRelations) => {
    navigate(`/projects/${project.id}`);
  };

  const handleCreateProject = (startDate: Date, endDate: Date) => {
    setCreateDialogOpen(true);
  };

  const handleProjectCreated = (projectId: string) => {
    navigate(`/projects/${projectId}/settings`);
  };

  const handleSubscribe = () => {
    calendarFeed.mutate(undefined, {
      onSuccess: () => {
        setPopoverOpen(true);
      },
    });
  };

  const handleCopyUrl = () => {
    if (!calendarFeed.data?.url) return;
    navigator.clipboard.writeText(calendarFeed.data.url).then(() => {
      toast.success(
        "Calendar URL copied! Paste it in Outlook, iPhone Calendar, or Google Calendar to subscribe.",
      );
    });
  };

  usePageHeader({ title: "Project Calendar" });

  const subscribeButton = (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={calendarFeed.isPending}
          onClick={handleSubscribe}
        >
          {calendarFeed.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <CalendarSync className="h-4 w-4 mr-2" />
          )}
          Subscribe
        </Button>
      </PopoverTrigger>
      {calendarFeed.data?.url && (
        <PopoverContent align="end" className="w-96">
          <div className="space-y-2">
            <p className="text-sm font-medium">Calendar Subscription URL</p>
            <p className="text-xs text-muted-foreground">
              Copy and paste this URL into Outlook, iPhone Calendar, or Google
              Calendar.
            </p>
            <div className="flex items-center gap-1.5">
              <Input
                readOnly
                value={calendarFeed.data.url}
                className="font-mono text-xs h-8"
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleCopyUrl}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );

  return (
    <div className="h-full flex flex-col animate-fade-in">
      {isLoading ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-8 w-full" />
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : (
        <CalendarGrid
          projects={(projects || []).filter((p) => p.status !== "rejected")}
          onProjectClick={handleProjectClick}
          onCreateProject={isEmployee ? handleCreateProject : undefined}
          headerActions={subscribeButton}
          employees={employees}
          absences={absences}
          bookings={bookings}
          bookedAvailability={bookedAvailability}
          projectIdsWithFunctions={projectIdsWithFunctions}
          visiblePeople={visiblePeople}
          onVisiblePeopleChange={handleVisiblePeopleChange}
        />
      )}

      <CreateProjectDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleProjectCreated}
      />
    </div>
  );
}
